import React, { useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploader — uploads file(s) to backend object storage via /api/upload.
 * Calls onChange(urls) with array of full file URLs.
 *
 * Optional marking mode (for product images):
 *   markable        — show "Main" / "Hover" mark buttons on each thumbnail
 *   primaryIndex    — index of the main display image
 *   hoverIndex      — index of the image shown on card hover
 *   onSetPrimary(i) / onSetHover(i) — mark callbacks
 */
export default function ImageUploader({
  value = [], onChange, multiple = true, kind = "general", maxFiles = 8,
  markable = false, primaryIndex = null, hoverIndex = null, onSetPrimary, onSetHover,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Local instant previews (object URLs) shown the moment a file is picked —
  // does not depend on the upload finishing or the server serving it back,
  // so the user always sees SOMETHING immediately instead of a blank box.
  const [pending, setPending] = useState([]); // [{ localUrl, status: "uploading"|"error", name }]

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    const picked = Array.from(files).slice(0, maxFiles - value.length);
    const localEntries = picked.map((f) => ({ localUrl: URL.createObjectURL(f), status: "uploading", name: f.name }));
    setPending((p) => [...p, ...localEntries]);

    const urls = [...value];
    for (let i = 0; i < picked.length; i++) {
      const f = picked[i];
      const entry = localEntries[i];
      if (f.size > 6 * 1024 * 1024) {
        toast.error(`${f.name} too large (max 6MB)`);
        setPending((p) => p.map((e) => e === entry ? { ...e, status: "error" } : e));
        continue;
      }
      const form = new FormData();
      form.append("file", f);
      form.append("kind", kind);
      try {
        const { data } = await api.post("/api/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(`${API_BASE.replace(/\/api$/, "")}${data.url}`);
        setPending((p) => p.filter((e) => e !== entry));
        URL.revokeObjectURL(entry.localUrl);
      } catch (e) {
        toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
        // Keep it visible with a red "failed" state instead of silently
        // disappearing — so a failure is never mistaken for "still loading".
        setPending((p) => p.map((en) => en === entry ? { ...en, status: "error" } : en));
      }
    }
    onChange(urls);
    setBusy(false);
  };

  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const dismissPending = (entry) => { URL.revokeObjectURL(entry.localUrl); setPending((p) => p.filter((e) => e !== entry)); };

  return (
    <div data-testid="image-uploader">
      <input ref={fileRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={(e) => upload(e.target.files)} />
      <div className="grid grid-cols-4 gap-2">
        {/* Uploading / failed previews — instant, local, independent of the server */}
        {pending.map((entry, i) => (
          <div key={`pending-${i}`} className={`relative aspect-square bg-pearl border overflow-hidden ${entry.status === "error" ? "border-red-500 border-2" : "border-line"}`} data-testid={`uploader-pending-${i}`}>
            <img src={entry.localUrl} alt="" className={`w-full h-full object-cover ${entry.status === "uploading" ? "opacity-60" : "opacity-40"}`} />
            {entry.status === "uploading" ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                <Loader2 className="animate-spin text-white" size={20} />
              </div>
            ) : (
              <button type="button" onClick={() => dismissPending(entry)} className="absolute inset-0 flex flex-col items-center justify-center bg-red-600/80 text-white text-[9px] gap-1" data-testid={`uploader-retry-${i}`}>
                <X size={16}/> Failed — tap to dismiss
              </button>
            )}
          </div>
        ))}
        {value.map((url, i) => (
          <div key={i} className={`relative aspect-square bg-pearl border overflow-hidden group ${markable && (i === primaryIndex || i === hoverIndex) ? "border-maroon border-2" : "border-line"}`}>
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 bg-maroon text-pearl rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 z-10">
              <X size={12} />
            </button>
            {markable && (
              <>
                {/* Active badges */}
                <div className="absolute top-1 left-1 flex flex-col gap-1 z-10">
                  {i === primaryIndex && <span className="bg-maroon text-pearl text-[8px] tracking-widest px-1.5 py-0.5 uppercase" data-testid={`badge-main-${i}`}>Main</span>}
                  {i === hoverIndex && <span className="bg-gold text-ink text-[8px] tracking-widest px-1.5 py-0.5 uppercase" data-testid={`badge-hover-${i}`}>Hover</span>}
                </div>
                {/* Mark buttons */}
                <div className="absolute bottom-0 inset-x-0 flex text-[9px] z-10">
                  <button type="button" onClick={() => onSetPrimary?.(i)} disabled={i === hoverIndex}
                    className={`flex-1 py-1 ${i === primaryIndex ? "bg-maroon text-pearl" : "bg-white/90 text-ink hover:bg-maroon hover:text-pearl disabled:opacity-40"}`}
                    data-testid={`set-main-${i}`}>
                    Main
                  </button>
                  <button type="button" onClick={() => onSetHover?.(i)} disabled={i === primaryIndex}
                    className={`flex-1 py-1 border-l border-line ${i === hoverIndex ? "bg-gold text-ink" : "bg-white/90 text-ink hover:bg-gold disabled:opacity-40"}`}
                    data-testid={`set-hover-${i}`}>
                    Hover
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        {value.length + pending.filter((e) => e.status === "uploading").length < maxFiles && (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="aspect-square border-2 border-dashed border-line hover:border-maroon flex flex-col items-center justify-center text-ash text-[10px]" data-testid="uploader-trigger">
            {busy ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
            <span className="mt-1">Upload</span>
          </button>
        )}
      </div>
    </div>
  );
}
